// LOOP-BREAKER in the in-memory API: Workforce › Loops (services/api/src/routes/loop-breaker.ts).
// The fake company has caught no loops (no sample data): the list is empty and an incident id is
// 404. Tests and e2e add incidents with `add`. The thresholds start at the platform default
// (services/api/src/domain/runs/loop-breaker/detect.ts) with no company override.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem } from "./fake";

export type FakeLoopIncident = OperationOutput<"loopIncidents.list">["data"][number];
type Thresholds = OperationOutput<"loopBreaker.settings.get">["platform"];
type Override = NonNullable<OperationOutput<"loopBreaker.settings.get">["override"]>;

export const FAKE_LOOP_ROUTES = [
  "loopIncidents.list",
  "loopIncidents.get",
  "loopIncidents.resolve",
  "loopBreaker.settings.get",
  "loopBreaker.settings.update",
] as const satisfies readonly RouteId[];

const FIELDS = [
  "enabled",
  "repeat_limit",
  "intent_repeat_limit",
  "stall_minutes",
  "similarity_pct",
] as const;
const RANGES: Record<Exclude<(typeof FIELDS)[number], "enabled">, [number, number]> = {
  repeat_limit: [2, 20],
  intent_repeat_limit: [2, 50],
  stall_minutes: [1, 240],
  similarity_pct: [50, 100],
};

function invalid(path: string, message: string): Response {
  return problem(422, "validation_failed", "Validation failed", `${path}: ${message}`, {
    errors: [{ path, code: "invalid", message }],
  });
}

export function createLoopsFake(deps: { now: () => Date }) {
  const incidents: (FakeLoopIncident & { company_id: string })[] = [];
  const platform: Thresholds = {
    enabled: true,
    repeat_limit: 3,
    intent_repeat_limit: 3,
    stall_minutes: 15,
    similarity_pct: 80,
  };
  const overrides = new Map<string, Override>();
  const view = (companyId: string) => {
    const o = overrides.get(companyId) ?? null;
    return {
      effective: { ...platform, ...(o ?? {}) } as Thresholds,
      platform: { ...platform },
      override: o ? { ...o } : null,
    };
  };
  const strip = ({ company_id: _c, ...x }: FakeLoopIncident & { company_id: string }) => x;
  const find = (companyId: string, id: string) =>
    incidents.find((x) => x.company_id === companyId && x.id === id) ?? null;

  const handlers: Partial<Record<RouteId, Handler>> = {
    "loopIncidents.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const status = url.searchParams.get("status");
      const membership = url.searchParams.get("membership_id");
      const limit = Number(url.searchParams.get("limit") ?? 50) || 50;
      const rows = incidents
        .filter(
          (x) =>
            x.company_id === principal.company.id &&
            (!status || x.status === status) &&
            (!membership || x.membership_id === membership),
        )
        .sort((a, b) => (a.detected_at < b.detected_at ? 1 : -1))
        .slice(0, limit);
      return json({
        data: rows.map(strip),
        totals: {
          incidents: rows.length,
          open: rows.filter((x) => x.status === "open").length,
          tokens: rows.reduce((n, x) => n + x.tokens_in + x.tokens_out, 0),
          cost_micros: rows.reduce((n, x) => n + x.cost_micros, 0),
        },
      });
    },
    "loopIncidents.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const x = find(principal.company.id, params.id ?? "");
      return x ? json(strip(x)) : notFound();
    },
    "loopIncidents.resolve": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const x = find(principal.company.id, params.id ?? "");
      if (!x) return notFound();
      const b = (body ?? {}) as { action?: unknown; hint?: unknown; seat_id?: unknown };
      if (b.action !== "retry" && b.action !== "reassign" && b.action !== "close")
        return invalid("action", "retry, reassign or close");
      const hint = typeof b.hint === "string" ? b.hint.trim() : "";
      if (b.action === "retry" && !hint) return invalid("hint", "what to do differently");
      if (b.action === "reassign" && typeof b.seat_id !== "string")
        return invalid("seat_id", "the seat the task was handed to");
      if (x.status !== "open") return problem(409, "conflict", "Conflict", "already resolved");
      const at = deps.now().toISOString();
      Object.assign(x, {
        status:
          b.action === "retry" ? "retried" : b.action === "reassign" ? "reassigned" : "closed",
        resolved_by_membership_id:
          principal.me.memberships.find((m) => m.company_id === principal.company.id)
            ?.membership_id ?? null,
        resolved_at: at,
        resolution_note: b.action === "retry" ? hint : null,
        reassigned_seat_id: b.action === "reassign" ? (b.seat_id as string) : null,
      });
      return json(strip(x));
    },
    "loopBreaker.settings.get": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(view(principal.company.id));
    },
    "loopBreaker.settings.update": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as Record<string, unknown>;
      for (const k of Object.keys(b))
        if (!(FIELDS as readonly string[]).includes(k)) return invalid(k, "unknown field");
      if (b.enabled !== undefined && b.enabled !== null && typeof b.enabled !== "boolean")
        return invalid("enabled", "true or false");
      for (const [k, [min, max]] of Object.entries(RANGES)) {
        const v = b[k];
        if (v === undefined || v === null) continue;
        if (typeof v !== "number" || !Number.isInteger(v) || v < min || v > max)
          return invalid(k, `a whole number from ${min} to ${max}`);
      }
      const next: Record<string, unknown> = { ...(overrides.get(principal.company.id) ?? {}) };
      for (const [k, v] of Object.entries(b)) {
        if (v === undefined) continue;
        if (v === null) delete next[k];
        else next[k] = v;
      }
      if (Object.keys(next).length) overrides.set(principal.company.id, next as Override);
      else overrides.delete(principal.company.id);
      return json(view(principal.company.id));
    },
  };

  return {
    handlers,
    /** Tests and e2e: a loop the breaker caught in a company. */
    add: (companyId: string, incident: FakeLoopIncident) =>
      incidents.push({ ...incident, company_id: companyId }),
  };
}
